/**
 * La grilla del folio: spans del mockup (grilla de 4 → base 12) y el título por defecto de cada
 * tipo de panel (clave i18n; el copy real vive en `informes.*`).
 */
import {PANEL_TYPES} from './informeModel';

export const PANEL_SPAN = Object.freeze({
    [PANEL_TYPES.KPI]: 3,
    [PANEL_TYPES.BARS]: 6,
    [PANEL_TYPES.PIE]: 6,
    [PANEL_TYPES.LINE]: 12,
    [PANEL_TYPES.TABLE]: 12,
    [PANEL_TYPES.TEXT]: 12,
});

export const DEFAULT_TITLE_KEY = Object.freeze({
    [PANEL_TYPES.KPI]: 'informes.panel.default_title.kpi',
    [PANEL_TYPES.BARS]: 'informes.panel.default_title.bars',
    [PANEL_TYPES.PIE]: 'informes.panel.default_title.pie',
    [PANEL_TYPES.LINE]: 'informes.panel.default_title.line',
    [PANEL_TYPES.TABLE]: 'informes.panel.default_title.table',
    // Lo usan el aviso de «agregaste» y el arrastre. En el panel, un texto sin título no muestra
    // ninguno (`panelTitle`): el cliente no tiene por qué leer «Texto» arriba de un párrafo.
    [PANEL_TYPES.TEXT]: 'informes.panel.default_title.text',
});

export function panelSpan(panel) {
    return PANEL_SPAN[panel.type] || 12;
}

export function defaultTitleKey(type) {
    return DEFAULT_TITLE_KEY[type] || 'informes.panel.default_title.generic';
}

export const GRID_COLUMNS = 12;

/**
 * El acomodo en filas de una lista de SPANS ya resueltos.
 *
 * Vive separado de `rowsOf` porque el PDF (`utils/pdfExport.js`) tiene los spans pero no los
 * paneles: el exportador recibe un ref por bloque, no el modelo. Compartir esta función es lo que
 * mantiene el PDF y la pantalla en la misma grilla — si el acomodo cambia, cambia en los dos
 * lados a la vez.
 */
export function rowsOfSpans(spans) {
    const rows = [];
    let current = null;
    (spans || []).forEach((rawSpan, index) => {
        // El clamp vive acá y en ningún otro lado: sin el piso, un span de 0 o negativo devolvía
        // filas con `used` negativo y `free` mayor que la grilla (review C-pdf-kpi-07).
        const span = Math.min(Math.max(1, rawSpan || GRID_COLUMNS), GRID_COLUMNS);
        if (!current || current.used + span > GRID_COLUMNS) {
            current = {startIndex: index, endIndex: index, used: span, free: GRID_COLUMNS - span};
            rows.push(current);
            return;
        }
        current.used += span;
        current.free = GRID_COLUMNS - current.used;
        current.endIndex = index;
    });
    return rows;
}

/**
 * En qué FILAS cae la grilla, y cuánto ancho libre queda en cada una.
 *
 * Las filas las arma el CSS (`.inf-grid` es un grid de 12 columnas con `grid-auto-flow: row`);
 * esto replica ese acomodo para poder ofrecer «agregar acá» donde de verdad hay lugar. Sin
 * `dense`: un panel que no entra en lo que resta de la fila abre una fila nueva y deja el hueco.
 *
 * Devuelve `[{startIndex, endIndex, used, free}]` — `endIndex` es el índice del ÚLTIMO panel de la
 * fila, así que el panel nuevo va en `endIndex + 1`. Una lista vacía devuelve `[]`.
 */
export function rowsOf(panels) {
    return rowsOfSpans((panels || []).map(panelSpan));
}
